import { useMemo, useState } from "react";
import { PAGE_SIZE_DEFAULT, clampPage, getPageCount, getPageRange, getPageSlice } from "../lib/pagination";

export function usePagination(items, { pageSize = PAGE_SIZE_DEFAULT, resetKey } = {}) {
  const list = useMemo(() => (Array.isArray(items) ? items : []), [items]);
  const [storedPage, setStoredPage] = useState(1);
  const [prevResetKey, setPrevResetKey] = useState(resetKey);

  // Reset to page 1 when resetKey changes. We adjust state during render (the
  // documented React pattern for resetting state in response to a changed
  // input) rather than in an effect, so the reset is synchronous and commits no
  // stale-page frame. The guard makes it idempotent (no render loop).
  let activePage = storedPage;
  if (resetKey !== prevResetKey) {
    setPrevResetKey(resetKey);
    setStoredPage(1);
    activePage = 1;
  }

  const pageCount = getPageCount(list.length, pageSize);
  const effectivePage = clampPage(activePage, pageCount);

  // Keep stored state in lockstep with the clamped page. Without this a live
  // list that shrinks below the current page (onSnapshot / archive / finalize)
  // would leave storedPage stale, which (a) makes the first Prev/Next click a
  // dead no-op and (b) snaps the viewer back to a now-stale deep page if the
  // list later grows again. Self-disarming once storedPage === effectivePage.
  if (storedPage !== effectivePage) {
    setStoredPage(effectivePage);
  }

  const pageItems = useMemo(
    () => getPageSlice(list, effectivePage, pageSize),
    [list, effectivePage, pageSize]
  );

  const { start: rangeStart, end: rangeEnd } = getPageRange(
    effectivePage,
    pageSize,
    list.length
  );

  return {
    pageItems,
    page: effectivePage,
    pageCount,
    total: list.length,
    rangeStart,
    rangeEnd,
    hasPrev: effectivePage > 1,
    hasNext: effectivePage < pageCount,
    // Navigate from the already-clamped effectivePage, not the raw storedPage,
    // so a single click always moves one page even right after a live shrink.
    goPrev: () => setStoredPage(clampPage(effectivePage - 1, pageCount)),
    goNext: () => setStoredPage(clampPage(effectivePage + 1, pageCount)),
    isPaged: pageCount > 1,
  };
}
